import copy from 'fast-clone';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import webApi from '../../../Lib/WebApi';
import { callApi, camelizeObjectKeys } from '../../Helpers/rxjsHelpers';

// Define specifics around which data should be retrieved from where for different test types
// * endpoint: default get/post/patch endpoint.
const worksheetTypeEndpointConfiguration = {
  'Assigned Maximum Dry Density': { endpoint: 'soil.assignedMddTests' },
  'Atterberg Limit': { endpoint: 'soil.atterbergLimitTests' },
  'Binder Content': { endpoint: 'asphalt.binderContentTests' },
  'Emerson Classification': { endpoint: 'soil.emersonClassificationTests' },
  'Hand Shear Vane': { endpoint: 'soil.handShearVaneTests' },
  'Sampling Certificate': { endpoint: 'soil.samplingCertificates' },
  'Converted Wet Density': { endpoint: 'soil.cwdTests' },
  'Dynamic Cone Penetrometer': { endpoint: 'soil.dynamicConePenetrometerTests' },
  Hydrometer: { endpoint: 'soil.psdHydrometerTests' },
  'Linear Shrinkage': { endpoint: 'soil.linearShrinkageTests' },
  'Los Angeles Value': { endpoint: 'soil.losAngelesValueTests' },
  'Maximum Dry Density': { endpoint: 'soil.mddTests' },
  'Maximum Dry Density Results': { endpoint: 'worksheets.getUnsupported' },
  'Moisture Condition Value': { endpoint: 'soil.moistureConditionValueTests' },
  'Moisture Content': { endpoint: 'soil.moistureContentTests' },
  'Moisture Content Dry Mass Reading': { endpoint: 'soil.moistureContentTests.dryMassReadings' },
  'Nuclear Field Density': { endpoint: 'soil.nuclearFieldDensityTests' },
  'Organic Content': { endpoint: 'soil.organicContentTests' },
  'Sieve Analysis': { endpoint: 'soil.psdSieveAnalysisTests' },
  'Plate Loading': { endpoint: 'soil.plateLoadingTests' },
  'Relative Compaction': { endpoint: 'soil.relativeCompactionTests' },
  'Soil Classification': { endpoint: 'soil.soilClassificationTests' },
  'Specific Gravity Coarse': { endpoint: 'soil.specificGravityTests.coarse' },
  Wash: { endpoint: 'soil.washTests' },
  'Sand Replacement': { endpoint: 'soil.sandReplacementTests' },
  'Asphalt Nuclear Field Density': { endpoint: 'asphalt.asphaltNuclearFieldDensityTests' },
  'Particle Density': { endpoint: 'soil.particleDensityTests' },
  // For unsupported worksheets, we use the generic endpoint to return basic data
  Worksheet: {
    endpoint: 'worksheets',
    getParams: { expandBody: false },
  },
};

const retrieveEndpointConfigOrThrow = (worksheetType, path) => {
  let config = copy(worksheetTypeEndpointConfiguration[worksheetType ?? 'Worksheet']);
  if (!config) {
    throw new Error(`The worksheet type "${worksheetType}" is not supported by this client.`);
  }
  config.endpoint = path ? `${config.endpoint}.${path}` : config.endpoint;
  if (!webApi[config.endpoint]) {
    throw new Error(`The worksheet type "${worksheetType}" does not have an endpoint '${config.endpoint}'.`);
  }
  return config;
};

const mapRootPathMapResponse = (r) => camelizeObjectKeys(r);

export const callApiPutWorksheet = (worksheetType, body, params, path) => {
  let { endpoint } = retrieveEndpointConfigOrThrow(worksheetType, path);
  return callApi(webApi[endpoint].put(body, params)).pipe(map((r) => (path ? r : mapRootPathMapResponse(r.Body))));
};

export const callApiPostWorksheet = (worksheetType, body, params, path) => {
  let { endpoint } = retrieveEndpointConfigOrThrow(worksheetType, path);
  return callApi(webApi[endpoint].post(body, params)).pipe(map((r) => (path ? r : mapRootPathMapResponse(r.Body))));
};

// Always include the header as it currently has status information (isChecked, onSignedReport)
export const callApiGetWorksheet = (worksheetType, worksheetId, includeHeader = true, path = null) => {
  let { endpoint, getParams: params } = retrieveEndpointConfigOrThrow(worksheetType, path);

  const gets = [];

  if (includeHeader) {
    // Include header & map its response to 'header' key.
    gets.push({ target: 'worksheets.header', mapResponse: (r) => ({ header: r }) });
  }

  // a request made to the root worksheet, should include everything required to render the worksheet
  // so map all props, also make sure they become camel cased.
  gets.push({
    target: endpoint,
    mapResponse: !path && mapRootPathMapResponse,
    params,
  });

  return forkJoin(
    // Make individual gets
    ...gets.map(({ target, params }) => callApi(webApi[target].get({ ...params, worksheetId }))),
    // Combine result Bodies into a single object.
    (...responses) =>
      responses.reduce(
        (previous, current, index) =>
          gets[index].mapResponse ? { ...previous, ...gets[index].mapResponse(current.Body) } : previous,
        {}
      )
  );
};

const testedByCheckedByEndpointConfiguration = {
  CheckedBy: { endpoint: 'worksheets.checkedBy' },
  TestedBy: { endpoint: 'worksheets.testedBy' },
};

export const callApiPutTestedByCheckedBy = (type, body, params) => {
  let { endpoint } = testedByCheckedByEndpointConfiguration[type];
  return callApi(webApi[endpoint].put(body, params));
};

export const callApiPostImportWorksheet = (body, params) => {
  return callApi(webApi['worksheets.import'].post(body, params));
};
